/**
 * Maps a pointer position onto the day column beneath it.
 *
 * The crosshair can't be driven by per-cell `mouseover`: a `LayoutRow` stacks the day cells, the
 * allocations layer and the create-button layer on top of each other, and the upper two swallow the
 * pointer over most of the row (a positioned allocation re-enables pointer events so it stays clickable,
 * and the create-button layer's spacer covers the whole allocation band without a handler). The row
 * instead listens once and works out the column from the pointer's x position.
 */

/**
 * The column index under `pointerX`, or `null` when the row has no days.
 *
 * A row lays its cells out as `flex flex-row gap-px` with every cell the same width, so
 * `rowWidth = daysCount * cellWidth + (daysCount - 1) * 1px`. Rearranged, the pitch from one cell's left
 * edge to the next is exactly `(rowWidth + 1) / daysCount` — which avoids converting the cell width from
 * rem to px or measuring individual cells.
 *
 * A pointer in one of the 1px gaps resolves to the column on its left, which is what it reads as
 * visually. Positions outside the row are clamped to the first/last column, so a pointer that leaves the
 * row sideways keeps the last sensible column rather than reporting nothing.
 */
export function columnIndexAtPointer(
    pointerX: number,
    rowLeft: number,
    rowWidth: number,
    daysCount: number,
): number | null {
    if (daysCount <= 0 || rowWidth <= 0) {
        return null;
    }

    const columnPitch = (rowWidth + 1) / daysCount;
    const index = Math.floor((pointerX - rowLeft) / columnPitch);

    return Math.min(Math.max(index, 0), daysCount - 1);
}
